import { useState, useEffect } from "react";
import apiFetch from "../../../../shared/services/apiClient";
import { downloadPDF } from "../../receipts/services/receiptService";

/**
 * Modal de Estado de Cuenta PDF.
 * Mismo diseño que el modal de ListReceipt, extraído para reutilizarse en CxC.
 *
 * Props:
 *   onClose()         — cerrar el modal
 *   filialPreselected — { id, idFilial } si ya se sabe la filial (desde el botón de la fila)
 */
export default function PdfEstadoModal({ onClose, filialPreselected = null }) {
  const [filiales, setFiliales] = useState([]);
  const [filial, setFilial] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFinal, setFechaFinal] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch("/api/residents/filial/")
      .then((r) => r.json())
      .then((data) => {
        const results = data.results ?? data;
        setFiliales(results);
        if (filialPreselected) {
          setFilial(String(filialPreselected.id));
        } else if (results.length > 0) {
          setFilial(String(results[0].id));
        }
      })
      .catch(() => {});
  }, [filialPreselected]);

  async function handleGenerate() {
    if (!filial || !fechaInicio || !fechaFinal) {
      setError("Todos los campos son obligatorios.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const blob = await downloadPDF({
        filial,
        fecha_inicio: fechaInicio,
        fecha_final: fechaFinal,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const label =
        filiales.find((f) => String(f.id) === filial)?.idFilial ?? filial;
      a.download = `estado_cuenta_${label}_${fechaInicio.replace(/\//g, "-")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch {
      setError(
        "Error al generar el PDF. Verificá los datos e intentá de nuevo.",
      );
    } finally {
      setLoading(false);
    }
  }

  const fieldLabel = {
    display: "block",
    color: "#64748b",
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 5,
  };
  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    background: "#253040",
    border: "1px solid #344155",
    borderRadius: 6,
    color: "#e2e8f0",
    fontSize: "0.85rem",
    colorScheme: "dark",
    outline: "none",
    transition: "border-color 0.15s",
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.65)",
          zIndex: 1000,
          backdropFilter: "blur(3px)",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          background: "#1a2535",
          border: "1px solid #2d3b4e",
          borderRadius: 12,
          padding: "24px 24px 20px",
          width: "min(440px,93vw)",
          zIndex: 1001,
          boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
        }}
      >
        {/* Cabecera */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 22,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                background: "rgba(249,115,22,0.15)",
                border: "1px solid rgba(249,115,22,0.25)",
                borderRadius: 9,
                padding: "9px 11px",
                color: "#f97316",
              }}
            >
              <i
                className="fa-solid fa-file-pdf"
                style={{ fontSize: "1.15rem" }}
              />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  color: "#f1f5f9",
                  fontSize: "1.02rem",
                  fontWeight: 700,
                }}
              >
                Estado de Cuenta
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  color: "#64748b",
                  fontSize: "0.78rem",
                }}
              >
                Genera el estado financiero por filial en PDF
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#4b5e75",
              cursor: "pointer",
              fontSize: "1.15rem",
            }}
            onMouseOver={(e) => (e.currentTarget.style.color = "#9ca3af")}
            onMouseOut={(e) => (e.currentTarget.style.color = "#4b5e75")}
          >
            ✕
          </button>
        </div>

        <div style={{ borderTop: "1px solid #2d3b4e", marginBottom: 20 }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Filial */}
          <div>
            <label style={fieldLabel}>Filial</label>
            <select
              value={filial}
              onChange={(e) => setFilial(e.target.value)}
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              {filiales.length === 0 && (
                <option value="">Cargando filiales…</option>
              )}
              {filiales.map((f) => (
                <option
                  key={f.id}
                  value={String(f.id)}
                  style={{ background: "#1a2535" }}
                >
                  {f.idFilial}
                </option>
              ))}
            </select>
          </div>

          {/* Fechas */}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={fieldLabel}>Fecha inicio</label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => {
                  setFechaInicio(e.target.value);
                  // Auto end-of-month for fechaFinal
                  if (e.target.value) {
                    const [y, m] = e.target.value.split('-').map(Number);
                    const fin = new Date(y, m, 0);
                    setFechaFinal(`${y}-${String(m).padStart(2,'0')}-${String(fin.getDate()).padStart(2,'0')}`);
                  }
                }}
                style={{...inputStyle, colorScheme:'dark'}}
                onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                onBlur={(e) => (e.target.style.borderColor = "#344155")}
              />
            </div>
            <div>
              <label style={fieldLabel}>Fecha final</label>
              <input
                type="date"
                value={fechaFinal}
                onChange={(e) => setFechaFinal(e.target.value)}
                style={{...inputStyle, colorScheme:'dark'}}
                onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                onBlur={(e) => (e.target.style.borderColor = "#344155")}
              />
            </div>
          </div>

          <p style={{ margin: 0, color: "#4b5e75", fontSize: "0.75rem" }}>
            <i className="fa-solid fa-circle-info" style={{ marginRight: 5 }} />
            Seleccioná el rango de fechas para el estado de cuenta.
          </p>

          {error && (
            <div
              style={{
                background: "rgba(248,113,113,0.1)",
                border: "1px solid rgba(248,113,113,0.3)",
                borderLeft: "3px solid #f87171",
                borderRadius: 6,
                padding: "8px 12px",
                color: "#fca5a5",
                fontSize: "0.82rem",
              }}
            >
              <i
                className="fa-solid fa-triangle-exclamation"
                style={{ marginRight: 5 }}
              />
              {error}
            </div>
          )}
        </div>

        <div
          style={{ borderTop: "1px solid #2d3b4e", margin: "20px 0 16px" }}
        />

        {/* Acciones */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid #2d3b4e",
              borderRadius: 7,
              color: "#64748b",
              fontSize: "0.84rem",
              cursor: "pointer",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "#4b5e75";
              e.currentTarget.style.color = "#9ca3af";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "#2d3b4e";
              e.currentTarget.style.color = "#64748b";
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleGenerate}
            disabled={loading || filiales.length === 0}
            style={{
              padding: "8px 20px",
              background: loading ? "rgba(249,115,22,0.6)" : "#f97316",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
            onMouseOver={(e) => {
              if (!loading) e.currentTarget.style.background = "#ea6b0c";
            }}
            onMouseOut={(e) => {
              if (!loading) e.currentTarget.style.background = "#f97316";
            }}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> Generando…
              </>
            ) : (
              <>
                <i className="fa-solid fa-download" /> Descargar PDF
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
